import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface AppointmentType {
  id: string;
  clinic_id: string;
  name: string;
  code: string;
  color: string;
  default_duration_minutes: number;
  requires_doctor: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useAppointmentTypes = (clinicId?: string) => {
  return useQuery({
    queryKey: ["appointment-types", clinicId],
    queryFn: async () => {
      let query = supabase
        .from("appointment_types")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (clinicId) {
        query = query.eq("clinic_id", clinicId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as AppointmentType[];
    },
    enabled: !!clinicId,
  });
};

export const useCreateAppointmentType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (type: Omit<AppointmentType, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("appointment_types")
        .insert(type)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointment-types"] });
      toast({
        title: "Success",
        description: "Appointment type created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateAppointmentType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AppointmentType> & { id: string }) => {
      const { data, error } = await supabase
        .from("appointment_types")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointment-types"] });
      toast({
        title: "Success",
        description: "Appointment type updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
